import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@providers/authProvider';
import { PrivateThemeProvider } from '@providers/privateThemeProvider';
import { PublicThemeProvider } from '@providers/publicThemeProvider';
import { AppStateProvider, useAppState } from '@state/AppStateProvider';
import ToastProvider from '@providers/toastProvider';
import ErrorBoundary from '@components/ErrorBoundary';
import RoleRoute from '@components/RoleRoute';
import BricklinkRoute from '@components/BricklinkRoute';
import MainLayout from '@layouts/MainLayout';
import 'react-toastify/dist/ReactToastify.css';
import { LoggerProvider, useLogger } from '@providers/loggerProvider';
import { useRecoveryRedirect } from '@hooks/ui/useRecoveryRedirect';
import { APP_PAGES_REGISTRY } from '@config/appPagesRegistry';

// Lazy load the public components
const PublicLayout = lazy(() => import('@pages/_public/PublicLayout'));
const LandingPage = lazy(() => import('@pages/_public/LandingPage'));
const BlogPage = lazy(() => import('@pages/_public/BlogPage/BlogPage'));
const BlogPostPage = lazy(() => import('@pages/_public/BlogPostPage/BlogPostPage'));
const BrickBuddyScanPage = lazy(() => import('@pages/_public/BrickBuddyScanPage/BrickBuddyScanPage'));
const VerifyEmail = lazy(() => import('@pages/_public/VerifyEmail'));
const ForgotPassword = lazy(() => import('@pages/_public/ForgotPassword'));
const ResetPassword = lazy(() => import('@pages/_public/ResetPassword'));
const PrivacyPolicy = lazy(() => import('@pages/_public/PrivacyPolicy'));
const NotFound = lazy(() => import('@pages/_public/NotFound'));
const PartIdentifier = lazy(() => import('@components/PartIdentifier'));

const renderProtectedPageElement = (appPage) => {
    const Component = appPage.component;
    if (appPage.role === 'admin') {
        return (
            <RoleRoute role="admin">
                <Component />
            </RoleRoute>
        );
    }
    if (appPage.requiresBricklink === true) {
        return (
            <BricklinkRoute pageLabel={appPage.label}>
                <Component />
            </BricklinkRoute>
        );
    }
    return <Component />;
};

const PUBLIC_PAGE_TITLES = {
    '/': 'Welcome | BrickBuddy',
    '/blog': 'Blog | BrickBuddy',
    '/scan': 'BrickBuddyScan | BrickBuddy',
    '/signin': 'Sign In | BrickBuddy',
    '/signup': 'Sign Up | BrickBuddy',
    '/verify-email': 'Verify Email | BrickBuddy',
    '/forgot-password': 'Forgot Password | BrickBuddy',
    '/reset-password': 'Reset Password | BrickBuddy',
    '/privacy': 'Privacy Policy | BrickBuddy'
};

const App = () => {
    // Remove useLogger from here as it's called before the provider is rendered
    // const logger = useLogger('App');

    return (
        <LoggerProvider config={{ enabled: true, minLogLevel: 'WARN' }}>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <AuthProvider>
                    <AppStateProvider>
                        <ErrorBoundary>
                            <ToastProvider>
                                <AppContent />
                            </ToastProvider>
                        </ErrorBoundary>
                    </AppStateProvider>
                </AuthProvider>
            </Router>
        </LoggerProvider>
    );
};

const PublicRoutes = () => {
    return (
        <PublicThemeProvider>
            <Routes>
                <Route element={<PublicLayout />}>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/scan" element={<BrickBuddyScanPage />} />
                    {/*
                     * /signin and /signup render the same LandingPage element so React Router
                     * preserves the LandingPage instance across the route change. The auth
                     * modal in PublicLayout floats over it, driven by URL pathname. The
                     * signup-live access gate now wraps the modal contents (inside
                     * PublicLayout) so a brief feature-flag fetch never blanks the page
                     * behind the modal. Verify, forgot and reset password stay ungated so
                     * existing users can recover their accounts while signup is closed.
                     */}
                    <Route path="/signin" element={<LandingPage />} />
                    <Route path="/signup" element={<LandingPage />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/app/*" element={<Navigate to="/signin" replace />} />
                    <Route path="*" element={<NotFound />} />
                </Route>
            </Routes>
        </PublicThemeProvider>
    );
};

const ProtectedRoutes = () => {
    const mainLayoutPages = APP_PAGES_REGISTRY.filter((appPage) => appPage.layout === 'main');
    const headerlessPages = APP_PAGES_REGISTRY.filter((appPage) => appPage.layout === 'headerless');

    return (
        <PrivateThemeProvider>
            <PartIdentifier />
            <Routes>
                {/* Routes that SHOULD use MainLayout (with header) */}
                <Route path="/app" element={<MainLayout />}>
                    {mainLayoutPages.map((appPage) => (
                        <Route
                            key={appPage.path}
                            path={appPage.path}
                            element={renderProtectedPageElement(appPage)}
                        />
                    ))}
                    <Route index element={<Navigate to="/app/home" replace />} />
                    <Route path="*" element={<NotFound />} />
                </Route>

                {/* Routes that should NOT use MainLayout (e.g., headerless) */}
                <Route path="app">
                    {headerlessPages.map((appPage) => (
                        <Route
                            key={appPage.path}
                            path={appPage.path}
                            element={renderProtectedPageElement(appPage)}
                        />
                    ))}
                </Route>

                {/* Catch-all for any other protected route - redirects to home */}
                <Route path="*" element={<Navigate to="/app/home" replace />} />
            </Routes>
        </PrivateThemeProvider>
    );
};

const isPublicBlogPath = (path) => {
    if (path === '/blog') {
        return true;
    }
    return path.startsWith('/blog/');
};

const AppContent = () => {
    const { setState, getState } = useAppState();
    const { isAuthenticated } = useAuth();
    const location = useLocation();
    const logger = useLogger('AppContent');
    const shouldRenderPublicRoutes = !isAuthenticated || isPublicBlogPath(location.pathname);

    useRecoveryRedirect();

    useEffect(() => {
        const path = location.pathname;
        let title = 'BrickBuddy';

        if (path.startsWith('/app/')) {
            const page = path.split('/app/')[1];
            if (page === 'userSets') {
                title = 'Sets | BrickBuddy';
            } else {
                const words = page.split('-');
                const capitalizedWords = words.map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1)
                );
                title = `${capitalizedWords.join(' ')} | BrickBuddy`;
            }
        } else if (path.startsWith('/blog/')) {
            if (/^\/blog\/[^/]+\/?$/.test(path)) return;
            title = 'Blog | BrickBuddy';
        } else {
            if (Object.prototype.hasOwnProperty.call(PUBLIC_PAGE_TITLES, path)) {
                title = PUBLIC_PAGE_TITLES[path];
            } else {
                title = 'BrickBuddy';
            }
        }

        document.title = title;
        try {
            const currentTitle = getState({ context: 'app', path: 'title' });
            if (currentTitle !== title) {
                setState({ context: 'app', path: 'title', value: title });
                logger.info('Updated page title via global state', 'useEffect', {
                    oldTitle: currentTitle,
                    newTitle: title
                });
            }
        } catch (error) {
            logger.error('Failed to set global title state', 'useEffect', { error });
        }
    }, [location, isAuthenticated, logger, setState, getState]);

    return (
        <div className="min-h-screen">
            <Suspense fallback={null}>
                {shouldRenderPublicRoutes ? <PublicRoutes /> : <ProtectedRoutes />}
            </Suspense>
        </div>
    );
};

export default App;
